import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Loader2, Copy, Check, AlertTriangle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  projectApiKeyCreateRequestSchema,
  type ProjectApiKeyCreateRequest,
  type ProjectApiKeyCreateResponse,
} from '@/schemas/project-api-key'

interface CreateApiKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isProcessing: boolean
  error: string | null
  createdKey: ProjectApiKeyCreateResponse | null
  onSubmit: (data: ProjectApiKeyCreateRequest) => void
}

export function CreateApiKeyDialog({
  open,
  onOpenChange,
  isProcessing,
  error,
  createdKey,
  onSubmit,
}: CreateApiKeyDialogProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProjectApiKeyCreateRequest>({
    resolver: zodResolver(projectApiKeyCreateRequestSchema),
  })

  const handleClose = (value: boolean) => {
    if (isProcessing) return
    if (!value) {
      setCopied(false)
      reset()
    }
    onOpenChange(value)
  }

  const handleCopy = async () => {
    if (!createdKey) return
    await navigator.clipboard.writeText(createdKey.key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-gray-900 border-gray-700/50">
        {createdKey ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-gray-100">
                {t('settings.apiKeys.create.successTitle')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-2">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                <p className="text-sm text-amber-400">
                  {t('settings.apiKeys.create.secretWarning')}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-300">{t('settings.apiKeys.create.keyLabel')}</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 p-3 rounded-lg bg-gray-800 border border-gray-700/50 text-sm text-gray-200 font-mono break-all">
                    {createdKey.key}
                  </code>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopy}
                    className="shrink-0 border-gray-700/50 text-gray-300 hover:bg-white/5"
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="action-indigo" onClick={() => handleClose(false)}>
                {t('settings.apiKeys.create.done')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-gray-100">
                {t('settings.apiKeys.create.title')}
              </DialogTitle>
              <DialogDescription className="text-gray-400">
                {t('settings.apiKeys.create.description')}
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="api-key-name" className="text-gray-300">
                  {t('settings.apiKeys.create.nameLabel')}
                </Label>
                <Input
                  id="api-key-name"
                  placeholder={t('settings.apiKeys.create.namePlaceholder')}
                  className="bg-gray-800/50 border-gray-700/50 text-gray-100"
                  {...register('name')}
                />
                {errors.name && (
                  <p className="text-sm text-red-400">{errors.name.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="api-key-description" className="text-gray-300">
                  {t('settings.apiKeys.create.descriptionLabel')}
                </Label>
                <Input
                  id="api-key-description"
                  placeholder={t('settings.apiKeys.create.descriptionPlaceholder')}
                  className="bg-gray-800/50 border-gray-700/50 text-gray-100"
                  {...register('description')}
                />
                {errors.description && (
                  <p className="text-sm text-red-400">{errors.description.message}</p>
                )}
              </div>

              {error && (
                <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <DialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  disabled={isProcessing}
                  className="border-gray-700/50 text-gray-300 hover:bg-white/5"
                >
                  {t('settings.apiKeys.create.cancel')}
                </Button>
                <Button type="submit" variant="action-indigo" disabled={isProcessing}>
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('settings.apiKeys.create.submitting')}
                    </>
                  ) : (
                    t('settings.apiKeys.create.submit')
                  )}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
